import React, { useEffect, useState, useRef } from 'react';
import { RefreshCw, Clock, MapPin, Phone, User, Bike, ShoppingBag, ChefHat, CheckCircle2, PackageCheck, Printer, Tv } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { printReceipt } from '../../lib/receipt';
import { PrepTimeControl } from '../../components/admin/PrepTimeControl';

const NEXT_STATUS = { queued: 'in_kitchen', in_kitchen: 'ready', ready: 'completed' };
const NEXT_LABEL = { queued: 'Start Preparing', in_kitchen: 'Mark Ready', ready: 'Mark Completed' };
const STATUS_META = {
  queued:     { label: 'New',        color: 'bg-red-500 text-white',      Icon: ChefHat },
  in_kitchen: { label: 'Preparing',  color: 'bg-amber-500 text-white',    Icon: ChefHat },
  ready:      { label: 'Ready',      color: 'bg-emerald-500 text-white',  Icon: CheckCircle2 },
};

const REFRESH_MS = 10000;

export default function AdminKitchen() {
  const [orders, setOrders] = useState([]);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem('wrappus_autoprint') === '1');
  const autoPrintRef = useRef(autoPrint);
  useEffect(() => { autoPrintRef.current = autoPrint; localStorage.setItem('wrappus_autoprint', autoPrint ? '1' : '0'); }, [autoPrint]);
  const prevIdsRef = useRef(new Set());
  const audioRef = useRef(null);
  const { toast } = useToast();

  const load = async () => {
    try {
      const r = await api.get('/admin/kitchen');
      const list = r.data || [];
      // Detect NEW orders since last poll
      const newIds = list.map((o) => o.id).filter((id) => !prevIdsRef.current.has(id));
      if (prevIdsRef.current.size > 0 && newIds.length > 0) {
        try { audioRef.current?.play?.().catch(() => {}); } catch (_) {}
        toast({ title: `${newIds.length} new order${newIds.length !== 1 ? 's' : ''}`, description: 'Check the board below.' });
        if (autoPrintRef.current) {
          newIds.forEach((id) => {
            const o = list.find((x) => x.id === id);
            if (o) printReceipt(o);
          });
        }
      }
      prevIdsRef.current = new Set(list.map((o) => o.id));
      setOrders(list);
      setLastLoaded(new Date());
    } catch (err) {
      toast({ title: 'Refresh failed', description: err?.response?.data?.detail || 'Retry in a moment' });
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const advance = async (o) => {
    const next = NEXT_STATUS[o.kitchenStatus || 'queued'];
    if (!next) return;
    try {
      await api.post(`/admin/orders/${o.id}/status`, { kitchenStatus: next });
      toast({ title: `Marked ${next.replace('_', ' ')}`, description: o.customerName || o.id.slice(0, 6) });
      load();
    } catch (err) {
      toast({ title: 'Update failed', description: err?.response?.data?.detail || '' });
    }
  };

  const reprintCloud = async (o) => {
    try {
      await api.post(`/admin/orders/${o.id}/print`);
      toast({ title: 'Sent to thermal printer', description: 'It prints on the next printer poll.' });
    } catch (err) {
      toast({ title: 'Could not queue print', description: err?.response?.data?.detail || '' });
    }
  };

  const grouped = { queued: [], in_kitchen: [], ready: [] };
  orders.forEach((o) => {
    const k = o.kitchenStatus || 'queued';
    if (grouped[k]) grouped[k].push(o);
  });

  return (
    <div data-testid="kitchen-board">
      {/* Silent chime (data-URI square wave) — best-effort on new orders */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRlYAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQwAAABTAABEAAAAJAAAABAAAA==" preload="auto" />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-[#0F1C2E]">Kitchen Order Board</h1>
          <p className="text-gray-500 text-sm mt-1">
            Live view of paid orders. Advance status as each order moves through prep.
            {lastLoaded && <span className="ml-2 text-xs">· Refreshed {lastLoaded.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600 inline-flex items-center gap-2">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="accent-[#ED2E2E]" data-testid="kitchen-autorefresh" />
            Auto-refresh (10s)
          </label>
          <label className="text-sm text-gray-600 inline-flex items-center gap-2">
            <input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} className="accent-[#ED2E2E]" data-testid="kitchen-autoprint" />
            Auto-print new orders
          </label>
          <a href="/admin/kitchen-tv" target="_blank" rel="noopener noreferrer" data-testid="kitchen-tv-link"
             className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0F1C2E] text-white text-sm font-semibold hover:bg-[#ED2E2E] transition">
            <Tv size={14} /> TV Mode
          </a>
          <button onClick={load} className="btn-wrappus text-sm" data-testid="kitchen-refresh"><RefreshCw size={14} /> Refresh</button>
        </div>
      </div>

      <div className="mb-6"><PrepTimeControl /></div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {['queued', 'in_kitchen', 'ready'].map((k) => {
          const M = STATUS_META[k];
          return (
            <div key={k} className={`rounded-2xl p-4 border ${grouped[k].length ? 'bg-white' : 'bg-gray-50'} border-gray-100`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${M.color}`}><M.Icon size={16} /></span>
                  <span className="font-heading font-bold text-lg text-[#0F1C2E]">{M.label}</span>
                </div>
                <span className="text-2xl font-heading font-black text-[#ED2E2E]" data-testid={`kitchen-count-${k}`}>{grouped[k].length}</span>
              </div>
              <div className="text-xs text-gray-500">
                {k === 'queued' && 'New orders needing preparation'}
                {k === 'in_kitchen' && 'Currently being prepared'}
                {k === 'ready' && 'Awaiting pickup / delivery'}
              </div>
            </div>
          );
        })}
      </div>

      {orders.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-gray-400 shadow-sm">
          <PackageCheck size={44} className="mx-auto mb-3 text-[#ED2E2E]/40" />
          <div className="font-semibold text-[#0F1C2E] text-lg mb-1">All caught up</div>
          <div className="text-sm">No open orders right now. New paid orders will show here automatically.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {orders.map((o) => <OrderCard key={o.id} order={o} onAdvance={advance} onReprint={reprintCloud} />)}
        </div>
      )}
    </div>
  );
}

function OrderCard({ order, onAdvance, onReprint }) {
  const kStatus = order.kitchenStatus || 'queued';
  const M = STATUS_META[kStatus] || STATUS_META.queued;
  const isDelivery = order.type === 'delivery';
  const created = order.created_at ? new Date(order.created_at) : (order.createdAt ? new Date(order.createdAt) : null);
  const minsAgo = created ? Math.max(0, Math.round((Date.now() - created.getTime()) / 60000)) : null;
  const agoLabel = minsAgo === null ? '' : minsAgo < 60 ? `${minsAgo}m` : minsAgo < 1440 ? `${Math.floor(minsAgo / 60)}h` : `${Math.floor(minsAgo / 1440)}d`;

  return (
    <div className={`bg-white rounded-2xl shadow-sm border-2 ${kStatus === 'queued' ? 'border-red-200' : kStatus === 'in_kitchen' ? 'border-amber-200' : 'border-emerald-200'} p-5`} data-testid={`kitchen-order-${order.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${M.color}`}><M.Icon size={14} /></span>
          <span className="text-xs font-bold uppercase tracking-wider">{M.label}</span>
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs font-mono text-gray-500">#{order.id.slice(0, 6).toUpperCase()}</span>
        </div>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${isDelivery ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'} inline-flex items-center gap-1`}>
          {isDelivery ? <Bike size={12} /> : <ShoppingBag size={12} />}
          {isDelivery ? 'Delivery' : 'Pickup'}
        </span>
      </div>

      <div className="mb-3 space-y-1 text-sm">
        <div className="flex items-center gap-2 font-heading font-bold text-[#0F1C2E]"><User size={14} className="text-gray-400" />{order.customerName || '—'}</div>
        {order.customerPhone && <a href={`tel:${order.customerPhone}`} className="flex items-center gap-2 text-gray-600 hover:text-[#ED2E2E]"><Phone size={14} className="text-gray-400" />{order.customerPhone}</a>}
        {isDelivery && order.deliveryAddress && (
          <div className="flex items-start gap-2 text-gray-600"><MapPin size={14} className="text-gray-400 mt-0.5 shrink-0" /><span className="line-clamp-2">{order.deliveryAddress}</span></div>
        )}
        {order.scheduledFor && (
          <div className="flex items-center gap-2 text-orange-600 font-semibold"><Clock size={14} />Scheduled: {new Date(order.scheduledFor).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' })}</div>
        )}
        {!order.scheduledFor && minsAgo !== null && (
          <div className="text-xs text-gray-500">Received {agoLabel} ago</div>
        )}
      </div>

      <div className="border-t border-gray-100 pt-3 mb-3 space-y-2">
        {(order.items || []).map((it, idx) => (
          <div key={idx} className="text-sm">
            <div className="flex justify-between font-semibold text-[#0F1C2E]">
              <span><span className="text-[#ED2E2E]">{it.qty}×</span> {it.name}</span>
              <span className="text-gray-500 text-xs">${(it.price * it.qty).toFixed(2)}</span>
            </div>
            {(it.modifiers || []).length > 0 && (
              <div className="pl-6 pt-1 space-y-0.5">
                {(() => {
                  const grouped = {};
                  (it.modifiers || []).forEach((m) => {
                    const g = m.groupName || 'Options';
                    grouped[g] = grouped[g] || [];
                    grouped[g].push(m);
                  });
                  return Object.entries(grouped).map(([g, mods]) => (
                    <div key={g} className="text-xs text-gray-600">
                      <span className="text-gray-400 uppercase tracking-wider mr-1">{g}:</span>
                      {mods.map((m, i) => (
                        <span key={i} className="mr-1">
                          {m.name}
                          {(m.price || 0) > 0 && <span className="text-gray-400"> (+${m.price.toFixed(2)})</span>}
                          {i < mods.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

      {order.notes && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 text-xs text-amber-900">
          <b>Notes:</b> {order.notes}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-100 pt-3 gap-2">
        <div className="font-heading font-black text-lg text-[#ED2E2E]">${(order.total || 0).toFixed(2)}</div>
        <div className="flex items-center gap-2">
          <button onClick={() => printReceipt(order)} data-testid={`kitchen-print-${order.id}`} title="Print receipt from this device"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-gray-300 text-[#0F1C2E] text-sm font-semibold hover:bg-gray-50 transition">
            <Printer size={14} /> Print
          </button>
          <button onClick={() => onReprint(order)} data-testid={`kitchen-reprint-${order.id}`} title="Send to the thermal printer"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FDF3EC] border border-[#ED2E2E]/30 text-[#ED2E2E] text-sm font-semibold hover:bg-[#ED2E2E] hover:text-white transition">
            <Printer size={14} />
          </button>
          {NEXT_STATUS[kStatus] && (
            <button onClick={() => onAdvance(order)} data-testid={`kitchen-advance-${order.id}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0F1C2E] text-white text-sm font-semibold hover:bg-[#ED2E2E] transition">
              <CheckCircle2 size={14} /> {NEXT_LABEL[kStatus]}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
